<template>
  <div class="tempo-control">
    <div class="tempo-display">
      <div class="tempo-controls">
        <button class="tempo-button minus" @click="emit('adjust', -1)" aria-label="Decrease tempo">
          <svg viewBox="0 0 24 24" class="icon">
            <path d="M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
        <div class="tempo-value">
          <span class="tempo-number">{{ tempo }}</span>
          <span class="tempo-unit">{{ bpmText }}</span>
        </div>
        <button class="tempo-button plus" @click="emit('adjust', 1)" aria-label="Increase tempo">
          <svg viewBox="0 0 24 24" class="icon">
            <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>
    </div>

    <div class="slider-container">
      <div class="custom-slider" ref="sliderRef" @mousedown="onPointerDown" @touchstart="onTouchStart">
        <div class="slider-track">
          <div class="slider-fill" :style="{ width: `${percentage}%` }"></div>
        </div>
        <div class="slider-thumb" :style="{ left: `${percentage}%` }" :class="{ 'dragging': isDragging }"
          @mousedown.stop="onPointerDown" @touchstart.stop="onTouchStart">
        </div>
      </div>
      <div class="tempo-range">
        <span>{{ MIN_TEMPO }}</span>
        <span>{{ MAX_TEMPO }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { MIN_TEMPO, MAX_TEMPO } from '@/constants/tempo'

const props = defineProps<{
  tempo: number
  bpmText: string
}>()

const emit = defineEmits<{
  'update:tempo': [value: number]
  'adjust': [value: number]
  'commit': []
}>()

const isDragging = ref(false)
const sliderRef = ref<HTMLElement | null>(null)

const percentage = computed(() => {
  return ((props.tempo - MIN_TEMPO) / (MAX_TEMPO - MIN_TEMPO)) * 100
})

const calculateTempoFromEvent = (event: MouseEvent | TouchEvent) => {
  const slider = sliderRef.value
  if (!slider) return props.tempo
  const rect = slider.getBoundingClientRect()
  const clientX = 'touches' in event ? event.touches[0]?.clientX ?? 0 : event.clientX
  let ratio = (clientX - rect.left) / rect.width
  ratio = Math.max(0, Math.min(1, ratio))
  const tempo = Math.round((MAX_TEMPO - MIN_TEMPO) * ratio + MIN_TEMPO)
  return Math.max(MIN_TEMPO, Math.min(MAX_TEMPO, tempo))
}

const beginDrag = (event: MouseEvent | TouchEvent, moveEvents: ['mousemove', 'mouseup'] | ['touchmove', 'touchend']) => {
  isDragging.value = true
  emit('update:tempo', calculateTempoFromEvent(event))

  const [moveEvent, endEvent] = moveEvents
  const handleMove = (e: MouseEvent | TouchEvent) => {
    e.preventDefault()
    if (isDragging.value) {
      emit('update:tempo', calculateTempoFromEvent(e))
    }
  }

  const handleEnd = () => {
    isDragging.value = false
    document.removeEventListener(moveEvent, handleMove)
    document.removeEventListener(endEvent, handleEnd)
    // 拖动结束后通知一次，供引擎立即按新速度重排
    emit('commit')
  }

  document.addEventListener(moveEvent, handleMove, { passive: false })
  document.addEventListener(endEvent, handleEnd, { once: true })
}

const onPointerDown = (event: MouseEvent) => {
  event.preventDefault()
  beginDrag(event, ['mousemove', 'mouseup'])
}

const onTouchStart = (event: TouchEvent) => {
  beginDrag(event, ['touchmove', 'touchend'])
}
</script>

<style scoped>
.tempo-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 32px;
}

.tempo-control {
  padding: 16px 0;
}

.tempo-display {
  margin-bottom: 32px;
}

.tempo-value {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 120px;
}

.tempo-number {
  font-size: 3em;
  font-weight: 700;
  color: var(--color-text, #000);
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
}

.tempo-unit {
  font-size: 0.9em;
  color: var(--color-text-secondary, #666);
  margin-top: 4px;
}

.tempo-button {
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 24px;
  background-color: #1989fa;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.tempo-button:hover {
  background-color: #40a9ff;
}

.tempo-button:active {
  transform: scale(0.95);
}

.tempo-button .icon {
  width: 24px;
  height: 24px;
}

.slider-container {
  padding: 0 8px;
  margin-top: 16px;
}

.custom-slider {
  position: relative;
  height: 36px;
  padding: 8px 0;
  cursor: pointer;
  touch-action: none;
  user-select: none;
}

.slider-track {
  position: relative;
  height: 4px;
  background-color: #e5e5e5;
  border-radius: 2px;
}

.slider-fill {
  position: absolute;
  height: 100%;
  background-color: #1989fa;
  border-radius: 2px;
  transition: width 0.1s ease;
}

.slider-thumb {
  position: absolute;
  top: 50%;
  width: 24px;
  height: 24px;
  background-color: white;
  border: 2px solid #1989fa;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: transform 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: grab;
  z-index: 1;
}

.slider-thumb:active {
  cursor: grabbing;
}

.slider-thumb.dragging {
  transform: translate(-50%, -50%) scale(1.2);
  background-color: #1989fa;
}

.tempo-range {
  display: flex;
  justify-content: space-between;
  color: var(--color-text-secondary, #666);
  font-size: 12px;
  padding: 0 12px;
  margin-top: 8px;
}

/* 适配移动设备 */
@media (max-width: 767px) {
  .tempo-control {
    padding: 8px 0;
  }

  .tempo-display {
    margin-bottom: 24px;
  }

  .tempo-controls {
    gap: 24px;
  }

  .tempo-value {
    min-width: 100px;
  }

  .tempo-number {
    font-size: 2.5em;
  }

  .tempo-button {
    width: 40px;
    height: 40px;
  }

  .tempo-button .icon {
    width: 20px;
    height: 20px;
  }
}

/* 暗色模式适配 */
@media (prefers-color-scheme: dark) {
  .tempo-number {
    color: var(--color-text, #fff);
  }

  .tempo-unit,
  .tempo-range {
    color: var(--color-text-secondary, #999);
  }

  .slider-track {
    background-color: #333;
  }

  .slider-thumb {
    background-color: #1989fa;
    border-color: #1989fa;
  }

  .slider-thumb.dragging {
    background-color: #40a9ff;
    border-color: #40a9ff;
  }
}
</style>
